import React, { useState } from 'react';
import { UserCheck, LogOut, BookOpen } from 'lucide-react';
import NotificationBell from './NotificationBell';
import GuideModal from './GuideModal';
import { useResponsive } from '../hooks/useResponsive';

const DashboardHeader = ({ user, titleText, onLogout }) => {
    const { width } = useResponsive();
    const isNarrow = width <= 767; // matches the 767/768 breakpoint spec exactly

    const [showGuide, setShowGuide] = useState(false);
    const tasksRoute = user.role === 'teamLead' ? '/team-lead/tasks'
        : user.role === 'admin' ? '/admin/tasks'
            : '/recruiter/tasks';

    const roleLabel = user.role === 'teamLead' ? 'TEAM LEAD'
        : user.role === 'admin' ? 'ADMIN'
            : 'RECRUITER';

    const logo = (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isNarrow ? '6px' : '10px',
            flexShrink: 0
        }}>
            <img
                src="/assets/logo.png"
                alt="Company Logo"
                style={{
                    width: isNarrow ? '100px' : '150px',
                    height: isNarrow ? '30px' : '45px',
                    objectFit: 'contain'
                }}
            />

            <button
                type="button"
                onClick={() => setShowGuide(true)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    padding: isNarrow ? '6px 8px' : '8px 12px',
                    background: '#f0f9ff', color: '#0369a1',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    fontWeight: '700',
                    fontSize: isNarrow ? '11px' : '13px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                }}
            >
                <BookOpen size={isNarrow ? 14 : 16} />
                Guide
            </button>
        </div>
    );

    const title = (
        <h1 style={{
            fontSize: isNarrow ? '14px' : '18px', fontWeight: '700', color: '#1f2937', margin: 0,
            justifySelf: isNarrow ? 'start' : 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>
            {titleText}
        </h1>
    );

    const bell = <NotificationBell userName={user.name} tasksRoute={tasksRoute} />;

    const userBadge = (
        <div style={{
            display: 'flex', alignItems: 'center', background: '#f0f9ff', color: '#0369a1',
            padding: isNarrow ? '5px 9px' : '6px 12px', borderRadius: '20px', fontSize: isNarrow ? '11px' : '13px',
            fontWeight: '600', gap: '6px', border: '1px solid #bae6fd', whiteSpace: 'nowrap'
        }}>
            <UserCheck size={14} />
            <span>{isNarrow ? user.name.split(' ')[0] : `${user.name} (${roleLabel})`}</span>
        </div>
    );

    const logoutBtn = (
        <button
            onClick={onLogout}
            style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: isNarrow ? '7px 9px' : '8px 12px', background: '#fee2e2', color: '#b91c1c',
                border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: isNarrow ? '11px' : '13px', cursor: 'pointer'
            }}
        >
            <LogOut size={14} /> {!isNarrow && 'Logout'}
        </button>
    );

    if (isNarrow) {
        // Row 1: Logo | Title | User      Row 2: Bell | · | Logout
        return (
            <div style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr auto', gridTemplateRows: 'auto auto',
                columnGap: '10px', rowGap: '10px', alignItems: 'center',
                marginBottom: '16px', background: '#fff', padding: '12px 14px',
                borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
                <div style={{ gridColumn: '1', gridRow: '1' }}>{logo}</div>
                <div style={{ gridColumn: '2', gridRow: '2' }}>{title}</div>
                <div style={{ gridColumn: '3', gridRow: '1', justifySelf: 'end' }}>{userBadge}</div>

                <div style={{ gridColumn: '1', gridRow: '2' }}>{bell}</div>
                <div style={{ gridColumn: '3', gridRow: '2', justifySelf: 'end' }}>{logoutBtn}</div>
            </div>
        );
    }

    // > 768px: single row — Logo | Title | Bell User Logout
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '16px', background: '#fff', padding: '16px',
            borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{logo}</div>
            {title}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {bell}
                {userBadge}
                {logoutBtn}
            </div>
            {showGuide && (
                <GuideModal onClose={() => setShowGuide(false)} />
            )}
        </div>
    );
};

export default DashboardHeader;